/**
 * BCP-47 negotiation. Pure - no MMKV, no expo-localization, no DOM - so
 * it runs under vitest in node and the precedence rules are testable
 * without a device.
 */
import { DEFAULT_LOCALE, LOCALES, OFFERED_LOCALES } from "./locales";

/** The sentinel meaning "follow the device", as opposed to a chosen tag. */
export const SYSTEM = "system" as const;

export type StoredChoice = string | typeof SYSTEM | null | undefined;

/**
 * Reduce a tag to the base language we ship.
 *
 * `fr-CM`, `fr_CA` and `FR` are all French; `zh-Hans-CN` is `zh`. We
 * ship one variant per language, so everything below the first subtag
 * is dropped rather than being matched more cleverly - a half-match on
 * a region we do not have is the same answer as no match.
 */
export function baseLanguage(tag: string): string {
  return String(tag || "")
    .replace(/_/g, "-")
    .split("-")[0]
    .toLowerCase();
}

/**
 * The first offered locale any of `tags` asks for, else the default.
 *
 * `tags` is in the OS's own preference order and that order is
 * respected exactly: a phone set to Portuguese-then-English gets
 * Portuguese, and the same phone with Portuguese removed gets English
 * rather than whatever happens to be first in the registry.
 *
 * Only OFFERED locales can be matched. A device set to Arabic gets
 * English today, which is the honest answer while the RTL sweep is
 * outstanding - see docs/i18n-plan.md section 9.
 */
export function negotiate(tags: readonly string[] | null | undefined): string {
  for (const tag of tags ?? []) {
    const base = baseLanguage(tag);
    if (!base) continue;
    const hit = OFFERED_LOCALES.find((l) => l.code === base);
    if (hit) return hit.code;
  }
  return DEFAULT_LOCALE;
}

/**
 * The whole precedence chain in one place.
 *
 * An explicit choice outranks the device, always and permanently: the
 * user has told us, and a device language change must not silently undo
 * that. `SYSTEM` is how they take it back.
 *
 * `account` is the locale the server holds. It is the weakest signal
 * because it may have been set from a DIFFERENT device, so it only
 * answers when this install has nothing of its own - a fresh login on a
 * new phone whose OS language we do not speak.
 */
export function resolveLocale(input: {
  stored?: StoredChoice;
  device?: readonly string[] | null;
  account?: string | null;
}): string {
  const { stored, device, account } = input;

  if (stored && stored !== SYSTEM) {
    const base = baseLanguage(stored);
    if (OFFERED_LOCALES.some((l) => l.code === base)) return base;
    // A stored tag we no longer offer (a language withdrawn, or a row
    // flipped to offered:false) must not pin the app to a catalogue
    // that is not there. Fall through and re-detect.
  }

  const detected = negotiate(device ?? []);
  if (detected !== DEFAULT_LOCALE) return detected;

  // The device asked for nothing we speak. Before settling for English,
  // ask what the account said - but only if the device did not
  // explicitly prefer English itself.
  const deviceWantsDefault = (device ?? []).some(
    (t) => baseLanguage(t) === DEFAULT_LOCALE,
  );
  if (!deviceWantsDefault && account) {
    const base = baseLanguage(account);
    if (OFFERED_LOCALES.some((l) => l.code === base)) return base;
  }

  return DEFAULT_LOCALE;
}

/**
 * Parse an `Accept-Language` header into an ordered tag list.
 *
 * Only used by the web surfaces; Django parses its own. q-values sort
 * descending and a missing q is 1.0, per RFC 9110.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .map((part) => {
      const [tag, ...params] = part.trim().split(";");
      const q = params
        .map((p) => p.trim())
        .find((p) => p.startsWith("q="));
      const weight = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag: tag.trim(), q: Number.isFinite(weight) ? weight : 0 };
    })
    .filter((e) => e.tag && e.tag !== "*" && e.q > 0)
    .sort((a, b) => b.q - a.q)
    .map((e) => e.tag);
}

/** Every locale a build should ship, offered or not. Used by tooling. */
export const ALL_CODES: readonly string[] = LOCALES.map((l) => l.code);
